// One Add-to-list menu for patents, journals and search results (UX-031).
// It replaces three near-identical local copies and adds the missing "New list…"
// row, so a first-time user with no lists can create one and save in one step.

import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bookmark, Check, ChevronDown, Plus } from 'lucide-react'
import { getPersonalLists, addListItem, createList } from '../../services/api'
import type { PersonalList } from '../../types'
import CreateListDialog from './CreateListDialog'

interface AddToListMenuProps {
  resourceId: string
  resourceType: 'document' | 'journal' | 'patent'
  title: string
  /** icon: bare bookmark; button: "Add to List ▾"; text: "Save to list" link-button */
  variant?: 'icon' | 'button' | 'text'
}

export default function AddToListMenu({ resourceId, resourceType, title, variant = 'icon' }: AddToListMenuProps) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: lists } = useQuery({
    queryKey: ['personal-lists'],
    queryFn: getPersonalLists,
    enabled: open,
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const finish = (listId: string) => {
    setAdded(listId)
    queryClient.invalidateQueries({ queryKey: ['list-items', listId] })
    setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
  }

  const handleAdd = async (list: PersonalList) => {
    setAddError(null)
    try {
      await addListItem(list.list_id, { resource_id: resourceId, resource_type: resourceType, title })
      finish(list.list_id)
    } catch (err) {
      // BUG-127: only a 409 means "already in this list" — a benign duplicate we
      // can treat as success. Every other outcome (403/500/network) is a real
      // failure and must be surfaced instead of silently swallowed.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
      if (e?.response?.status === 409) {
        finish(list.list_id)
      } else {
        setAddError(e?.response?.data?.error || e?.message || 'Failed to add to list')
      }
    }
  }

  const handleCreate = async (name: string, description: string) => {
    const list = await createList({ name, description })
    queryClient.invalidateQueries({ queryKey: ['personal-lists'] })
    queryClient.invalidateQueries({ queryKey: ['lists'] })
    setCreating(false)
    await handleAdd(list)
  }

  const trigger =
    variant === 'button' ? (
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-sky-200 rounded-lg hover:bg-white/50 transition-colors"
      >
        <Bookmark size={14} />
        Add to List
        <ChevronDown size={12} />
      </button>
    ) : variant === 'text' ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-medium text-iflow-600 hover:text-iflow-700"
      >
        <Bookmark size={12} />
        Save to list
      </button>
    ) : (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Add to list"
        className="p-1.5 text-gray-500 hover:text-iflow-600 flex-shrink-0"
        title="Add to list"
      >
        <Bookmark size={14} />
      </button>
    )

  return (
    <div className="relative" ref={ref}>
      {trigger}
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1 w-56 light-card shadow-lg z-20 py-1">
          {lists && lists.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-500">No lists yet.</p>
          )}
          {lists?.map((l) => (
            <button
              key={l.list_id}
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); void handleAdd(l) }}
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-white/50"
            >
              <span className="truncate">{l.name}</span>
              {added === l.list_id && <Check size={12} className="text-green-500 flex-shrink-0" />}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={(e) => { e.stopPropagation(); setCreating(true) }}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-iflow-700 hover:bg-white/50 border-t border-sky-200/50"
          >
            <Plus size={12} /> New list…
          </button>
          {addError && (
            <p data-testid="add-list-error" className="px-3 py-2 text-xs text-red-600 border-t border-sky-200/50">{addError}</p>
          )}
        </div>
      )}
      {creating && (
        <CreateListDialog
          title="New list"
          submitLabel="Create and save"
          onSubmit={handleCreate}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}
